import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, AlertTriangle, XCircle, Activity, RefreshCw } from 'lucide-react';
import TopBar from '../components/TopBar.jsx';
import { apiGet } from '../lib/api.js';

const SUBSYSTEMS = [
  { key: 'mongo', label: 'MongoDB', desc: 'Lead & user data store' },
  { key: 'numberHealth', label: 'Number Health', desc: 'In-house dialer reputation & rotation' },
  { key: 'twilio', label: 'Twilio', desc: 'SMS, MMS, fallback voice' },
  { key: 'dnc', label: 'DNC / TCPA', desc: 'Blacklist Alliance scrub' },
  { key: 'sentry', label: 'Sentry', desc: 'Error monitoring (scaffolded)' },
];

function StatusBadge({ ok, status }) {
  if (ok && status !== 'degraded') return <span className="flex items-center gap-1 text-emerald-400 text-xs"><CheckCircle2 className="w-3.5 h-3.5"/>operational</span>;
  if (status === 'degraded') return <span className="flex items-center gap-1 text-amber-400 text-xs"><AlertTriangle className="w-3.5 h-3.5"/>degraded</span>;
  return <span className="flex items-center gap-1 text-red-400 text-xs"><XCircle className="w-3.5 h-3.5"/>down</span>;
}

export default function StatusPage() {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['status'],
    queryFn: () => apiGet('/status'),
    refetchInterval: 15000,
  });

  const overall = data?.status || 'unknown';
  const checks = data?.checks || {};
  const allOk = overall === 'ok';

  return (
    <div className="space-y-4 max-w-[1100px] mx-auto">
      <TopBar title="System Status" subtitle="Live health of every CIVIX subsystem — auto-refreshes every 15s" />

      {/* Overall banner */}
      <div className={`panel p-6 border ${allOk ? 'border-emerald-400/40' : 'border-amber-400/40'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center ${allOk ? 'bg-emerald-400/20' : 'bg-amber-400/20'}`}>
              {allOk ? <CheckCircle2 className="w-8 h-8 text-emerald-400"/> : <AlertTriangle className="w-8 h-8 text-amber-400"/>}
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-white/50">Overall</div>
              <div className="font-display text-3xl font-bold">
                {allOk ? 'All systems operational' : overall === 'degraded' ? 'Degraded performance' : 'Issue detected'}
              </div>
              <div className="text-sm text-white/50 mt-1">
                Version {data?.version || '1.0.0'} · {data?.time ? new Date(data.time).toLocaleString() : '—'}
              </div>
            </div>
          </div>
          <button onClick={() => refetch()} className="btn-ghost flex items-center gap-2">
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Subsystem rows */}
      <div className="panel p-0 overflow-hidden">
        <div className="grid grid-cols-12 px-5 py-3 border-b border-civix-border text-[10px] uppercase tracking-wider text-white/50">
          <div className="col-span-5">Subsystem</div>
          <div className="col-span-3">Status</div>
          <div className="col-span-2">Latency</div>
          <div className="col-span-2 text-right">Detail</div>
        </div>
        {SUBSYSTEMS.map((s) => {
          const c = checks[s.key] || {};
          return (
            <div key={s.key} className="grid grid-cols-12 px-5 py-4 border-b border-civix-border last:border-b-0 items-center hover:bg-white/2">
              <div className="col-span-5 flex items-center gap-3">
                <Activity className="w-4 h-4 text-civix-cyan" />
                <div>
                  <div className="font-medium">{s.label}</div>
                  <div className="text-xs text-white/50">{s.desc}</div>
                </div>
              </div>
              <div className="col-span-3">
                {isLoading ? <span className="text-xs text-white/40">checking…</span> : <StatusBadge ok={c.ok} status={c.status} />}
              </div>
              <div className="col-span-2 font-mono text-sm text-white/70">
                {c.latencyMs != null ? `${c.latencyMs}ms` : '—'}
              </div>
              <div className="col-span-2 text-right text-xs text-white/50">
                {c.status || '—'}
                {c.provider && <div>{c.provider}</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Endpoints panel */}
      <div className="panel p-5">
        <div className="font-semibold mb-2">Health endpoints</div>
        <div className="grid md:grid-cols-3 gap-3 text-sm">
          <div className="panel-light p-3">
            <div className="font-mono text-civix-cyan">GET /healthz</div>
            <div className="text-xs text-white/50 mt-1">Liveness probe — always 200 if process is up</div>
          </div>
          <div className="panel-light p-3">
            <div className="font-mono text-civix-cyan">GET /readyz</div>
            <div className="text-xs text-white/50 mt-1">Readiness probe — only 200 once Mongo connects</div>
          </div>
          <div className="panel-light p-3">
            <div className="font-mono text-civix-cyan">GET /api/status</div>
            <div className="text-xs text-white/50 mt-1">Full multi-subsystem report (powers this page)</div>
          </div>
        </div>
      </div>
    </div>
  );
}
